import { EXEMPLES, IDS, INSTANT, ROUTES, uuid, type RoleCode, type RouteContrat, type Session } from '../api/contrat.js';

/**
 * Serveur de mocks de la coquille : un `fetch` simulé, dérivé des exemples du contrat (« Famille Bélénus »), sans
 * service worker ni MSW. Il sert le développement (`LAKOU_MOCKS=1`) et les tests. Les handlers MSW du contrat
 * (`creerHandlers`) restent disponibles pour le navigateur quand RFC-050 aura déclaré `msw` dans le paquet web.
 * Toutes les données sont fictives (adresses en .test).
 */

type Utilisateur = Session['user'];

export const JETON_EXPIRE = 'expire0000';

interface Fiche {
  id: string;
  nom: string;
  email: string;
  kind: Utilisateur['kind'];
}

const FICHES: Readonly<Record<RoleCode, Fiche>> = {
  admin: { id: uuid(0x410), nom: 'Administration MAAT', email: 'administration@cabinet-maat.test', kind: 'staff' },
  dirigeante: { id: IDS.isabelle, nom: 'Isabelle F.', email: 'isabelle@cabinet-maat.test', kind: 'staff' },
  assistante: { id: IDS.brigitte, nom: 'Brigitte', email: 'brigitte@cabinet-maat.test', kind: 'staff' },
  agent: { id: uuid(0x411), nom: 'Agent MAAT', email: 'agent@cabinet-maat.test', kind: 'staff' },
  clerc: { id: IDS.cynthia, nom: 'Cynthia', email: 'cynthia@etude-principale.test', kind: 'external' },
  notaire: { id: uuid(0x412), nom: 'Maître Exemple', email: 'notaire@etude-principale.test', kind: 'external' },
  mandant: { id: uuid(0x413), nom: 'Josiane M.', email: 'josiane.m@exemple.test', kind: 'portal' },
  heritier: { id: uuid(0x40a), nom: 'Marc B.', email: 'marc.b@exemple.test', kind: 'portal' },
  prestataire: { id: uuid(0x414), nom: 'Prestataire Exemple', email: 'prestataire@exemple.test', kind: 'external' },
  representant_legal: { id: uuid(0x415), nom: 'Représentant Exemple', email: 'representant@exemple.test', kind: 'portal' },
  contact_delegue: { id: uuid(0x416), nom: 'Contact Exemple', email: 'contact@exemple.test', kind: 'portal' },
};

const utilisateur = (role: RoleCode): Utilisateur => {
  const f = FICHES[role];
  return {
    id: f.id,
    email: f.email,
    displayName: f.nom,
    kind: f.kind,
    status: 'actif',
    roles: [{ role, scopeType: 'global', scopeId: null, validUntil: null }],
  };
};

export function sessionPourRole(role: RoleCode, options: { incarne?: RoleCode } = {}): Session {
  const base = { permissions: ['session:read:self'], expiresAt: '2026-10-14T18:00:00-04:00' };
  if (!options.incarne) return { ...base, user: utilisateur(role), effectiveUser: null, impersonation: null };
  const cible = utilisateur(options.incarne);
  return {
    ...base,
    user: utilisateur(role),
    effectiveUser: cible,
    impersonation: { id: IDS.impersonation, mode: 'read', targetDisplayName: cible.displayName, startedAt: INSTANT },
  };
}

const ROLES_CONNUS = Object.keys(FICHES) as RoleCode[];
const estRole = (valeur: string | null): valeur is RoleCode => valeur !== null && (ROLES_CONNUS as string[]).includes(valeur);

/** Session de démonstration choisie dans l’adresse : `?role=heritier` ou `?role=admin&incarne=heritier` ; sinon aucun rôle. */
export function sessionDepuisAdresse(recherche: string): Session | null {
  const parametres = new URLSearchParams(recherche);
  const role = parametres.get('role');
  if (!estRole(role)) return null;
  const incarne = parametres.get('incarne');
  return sessionPourRole(role, estRole(incarne) ? { incarne } : {});
}

export interface OptionsMocks {
  /** Session ouverte au départ ; `null` pour un visiteur ; par défaut, celle de l’exemple du contrat. */
  session?: Session | null;
  /** Session ouverte par un lien magique valide ; par défaut, celle de l’exemple du contrat (Marc B.). */
  sessionApresLien?: Session;
  /** Simule une coupure réseau : tout appel échoue comme un `fetch` hors ligne. */
  horsLigne?: boolean;
}

interface Route {
  route: RouteContrat;
  motif: RegExp;
  nbParametres: number;
}

const ECHAPPER = /[.*+?^${}()|[\]\\]/g;

const ROUTES_TRIEES: readonly Route[] = ROUTES.map((route) => ({
  route,
  nbParametres: (route.chemin.match(/\{/g) ?? []).length,
  motif: new RegExp(
    `^${route.chemin
      .split(/\{[a-zA-Z]+\}/)
      .map((s) => s.replace(ECHAPPER, '\\$&'))
      .join('[^/]+')}$`,
  ),
})).sort((a, b) => a.nbParametres - b.nbParametres);

const entetesJson = { 'content-type': 'application/json' };
const entetesProbleme = { 'content-type': 'application/problem+json' };

const reponseJson = (corps: unknown, status: number): Response =>
  corps === undefined || status === 204
    ? new Response(null, { status })
    : new Response(JSON.stringify(corps), { status, headers: entetesJson });

const probleme = (status: number, code: string, title: string): Response =>
  new Response(JSON.stringify({ type: 'urn:lakou:erreur', title, status, code }), { status, headers: entetesProbleme });

const jetonDuCorps = (corps: unknown): string | undefined => {
  if (typeof corps !== 'string') return undefined;
  try {
    const lu: unknown = JSON.parse(corps);
    return typeof lu === 'object' && lu !== null && 'token' in lu && typeof lu.token === 'string' ? lu.token : undefined;
  } catch {
    return undefined;
  }
};

export function creerFetchMocke(options: OptionsMocks = {}): typeof fetch {
  const exemple = (id: string) => EXEMPLES[id]?.reponse;
  const sessionExemple = exemple('auth.moi')?.corps as Session;
  let session: Session | null = options.session === undefined ? sessionExemple : options.session;

  return (async (entree: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    if (options.horsLigne) throw new TypeError('Failed to fetch');
    const url = new URL(typeof entree === 'string' ? entree : entree instanceof URL ? entree.href : entree.url, 'http://mocks.local');
    const methode = (init?.method ?? 'GET').toLowerCase();
    const trouvee = ROUTES_TRIEES.find((r) => r.route.methode === methode && r.motif.test(url.pathname));
    if (!trouvee) return probleme(404, 'NOT_FOUND', 'Route inconnue du contrat');
    const { id } = trouvee.route;

    if (id === 'auth.moi') return session ? reponseJson(session, 200) : probleme(401, 'UNAUTHENTICATED', 'Session absente');
    if (id === 'auth.connexion') session = sessionExemple;
    if (id === 'auth.lienMagique') {
      if (jetonDuCorps(init?.body) === JETON_EXPIRE) return probleme(410, 'LINK_EXPIRED', 'Lien expiré');
      session = options.sessionApresLien ?? (exemple('auth.lienMagique')?.corps as Session);
      return reponseJson(session, 200);
    }
    if (id === 'auth.deconnexion') session = null;
    if (id === 'impersonation.terminer' && session) session = { ...session, effectiveUser: null, impersonation: null };

    const ex = exemple(id);
    return ex ? reponseJson(ex.corps, ex.status) : probleme(404, 'NOT_FOUND', 'Aucun exemple pour cette route');
  }) as typeof fetch;
}
